(() => {
  if (customElements.get('rg10000-gallery')) return;
  customElements.define('rg10000-gallery', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.images = [...this.querySelectorAll('[data-rg-image]')];
      this.models = [...this.querySelectorAll('[data-rg-model]')];
      this.videos = [...this.querySelectorAll('[data-rg-video]')];
      const configuredVideo = this.videos.find(video => video.dataset.mediaId === 'configured-video');
      if (configuredVideo) this.videos = [configuredVideo, ...this.videos.filter(video => video !== configuredVideo)];
      this.thumbs = [...this.querySelectorAll('[data-rg-thumb]')];
      this.pageSize = Number(this.dataset.pageSize) || 10;
      if (new URLSearchParams(window.location.search).get('rg_media') === 'images') {
        this.models.forEach(model => model.remove());
        this.models = [];
        this.videos = [];
        this.querySelector('[data-rg-videos]')?.remove();
        this.querySelector('.rg-gallery__modes')?.remove();
        this.pageSize = 10;
        this.querySelector('.rg-gallery__thumb-grid')?.style.setProperty('--rg-thumb-columns', '10');
      }
      this.page = 0;
      this.galleryIndex = 0;
      this.galleryItems = this.images;
      this.dataset.hasModel = String(this.models.length > 0);
      this.timeline = [...this.images, ...this.videos, ...this.models];
      this.active = this.images[0] || this.videos[0] || this.models[0];
      this.mode = this.images.length ? 'image' : this.videos.length ? 'video' : this.models.length ? 'model' : 'image';
      this.mobile = matchMedia('(max-width: 749px)');
      this.rail = this.querySelector('[data-rg-images]');
      this.addEventListener('click', event => {
        const expand = event.target.closest('[data-rg-expand]');
        if (expand) { if (this.expandedDialog?.open) this.expandedDialog.close(); else this.expandModel(expand.closest('[data-rg-model]')); return; }
        const thumb = event.target.closest('[data-rg-thumb]');
        const group = event.target.closest('[data-rg-thumb-step]');
        const move = event.target.closest('[data-rg-main-step]');
        const mode = event.target.closest('[data-rg-mode]');
        if (thumb) this.select(this.galleryItems[Number(thumb.dataset.rgThumb)]);
        if (group) { this.page = Math.max(0, Math.min(this.pageCount() - 1, this.page + Number(group.dataset.rgThumbStep))); this.renderThumbs(); }
        if (move && this.mode === 'image') this.select(this.images[this.galleryIndex + Number(move.dataset.rgMainStep)]);
        if (mode) this.select(mode.dataset.rgMode === 'image' ? this.galleryItems[this.galleryIndex] : mode.dataset.rgMode === 'model' ? this.models[0] : this.videos[0]);
      }, { signal });
      this.rail?.addEventListener('scroll', () => {
        if (!this.mobile.matches || this.mode !== 'image' || this.expandedDialog?.open) return;
        const index = Math.round(this.rail.scrollLeft / Math.max(1, this.rail.clientWidth));
        if (this.galleryItems[index] && this.active !== this.galleryItems[index]) this.select(this.galleryItems[index], false);
      }, { signal, passive: true });
      this.addEventListener('keydown', event => {
        if (!event.target.closest('.rg-gallery__modes, .rg-gallery__thumbs')) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.select(this.timeline[this.timeline.indexOf(this.active) + (event.key === 'ArrowRight' ? 1 : -1)]);
      }, { signal });
      this.mobile.addEventListener('change', () => this.render(true), { signal });
      this.guides = new Map();
      this.models.forEach(model => model.addEventListener('pointerdown', event => { if (event.isTrusted) this.stopGuide(model); }, { signal }));
      if (typeof subscribe === 'function') {
        this.unsubscribe?.();
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId !== this.dataset.section || !data.variant) return;
          const mediaId = data.variant.featured_media?.id;
          const imageId = data.variant.featured_image?.id;
          const item = this.timeline.find(node => (mediaId && node.dataset.mediaId === String(mediaId)) || (imageId && node.dataset.imageId === String(imageId)));
          if (item) this.select(item);
        });
      }
      document.addEventListener('fullscreenchange', () => {
        if (this.expandedDialog && document.fullscreenElement === this.expandedDialog) this.wasFullscreen = true;
        else if (this.wasFullscreen) { this.wasFullscreen = false; if (this.expandedDialog?.open) this.expandedDialog.close(); }
      }, { signal });
      this.render(true);
    }
    expandModel(model) {
      if (!model) return;
      const placeholder = document.createComment('RG10000 model position');
      model.before(placeholder);
      const dialog = document.createElement('dialog');
      dialog.className = 'rg-gallery__expanded'; dialog.setAttribute('aria-label', 'Expanded 3D model');
      const close = document.createElement('button'); close.type = 'button'; close.className = 'rg-gallery__expanded-close'; close.dataset.rgExpand = ''; close.textContent = '×'; close.setAttribute('aria-label', 'Close 3D view');
      dialog.append(close, model); this.append(dialog); this.expandedDialog = dialog;
      const expand = model.querySelector('[data-rg-expand]'); expand?.setAttribute('aria-label', 'Close expanded 3D view');
      dialog.addEventListener('close', () => {
        if (document.fullscreenElement === dialog) document.exitFullscreen()?.catch(() => {});
        placeholder.replaceWith(model); dialog.remove(); this.expandedDialog = null; this.render(true);
        expand?.setAttribute('aria-label', 'Expand 3D view'); expand?.focus();
      }, { once: true });
      dialog.showModal();
      dialog.requestFullscreen?.()?.catch(() => {});
    }
    pageCount() { return Math.max(1, Math.ceil(this.galleryItems.length / this.pageSize)); }
    select(item, scroll = true) {
      if (!item) return;
      this.active = item;
      this.mode = item.hasAttribute('data-rg-model') ? 'model' : item.hasAttribute('data-rg-video') ? 'video' : 'image';
      if (this.mode === 'image') { this.galleryIndex = this.galleryItems.indexOf(item); this.page = Math.floor(this.galleryIndex / this.pageSize); }
      this.render(scroll);
    }
    renderThumbs() {
      this.thumbs.forEach((thumb, index) => {
        thumb.hidden = Math.floor(index / this.pageSize) !== this.page;
        thumb.setAttribute('aria-pressed', String(this.mode === 'image' && this.galleryIndex === index));
      });
      this.querySelectorAll('[data-rg-thumb-step]').forEach(button => {
        button.hidden = this.pageCount() <= 1;
        button.disabled = Number(button.dataset.rgThumbStep) < 0 ? this.page === 0 : this.page >= this.pageCount() - 1;
      });
    }
    render(scroll) {
      this.dataset.mode = this.mode;
      if (this.rail) this.rail.hidden = this.mode !== 'image';
      this.images.forEach(image => { image.hidden = !this.mobile.matches && image !== this.active; });
      this.models.forEach(model => { model.hidden = model !== this.active; });
      const videoGroup = this.querySelector('[data-rg-videos]');
      if (videoGroup) videoGroup.hidden = this.mode !== 'video';
      this.videos.forEach(video => {
        video.hidden = video !== this.active;
        if (video !== this.active) {
          video.querySelector('video')?.pause();
          // Removing the iframe stops external playback without cross-origin scripting.
          video.querySelector('iframe')?.remove();
        }
      });
      if (this.mode !== 'model') this.models.forEach(model => model.querySelector('model-viewer')?.pause?.());
      if (this.mode === 'model') { this.loadModel(this.active); this.guides?.get(this.active)?.start?.(); }
      if (this.mode === 'video' && this.active) {
        if (!this.active.querySelector('video,iframe')) this.active.append(this.active.querySelector('template').content.cloneNode(true));
        this.active.querySelector('video')?.play()?.catch(() => {});
      }
      const index = this.galleryIndex;
      this.querySelectorAll('[data-rg-main-step]').forEach(button => {
        button.hidden = this.mode !== 'image' || this.images.length <= 1;
        button.disabled = Number(button.dataset.rgMainStep) < 0 ? index <= 0 : index >= this.images.length - 1;
      });
      const count = this.querySelector('[data-rg-count]');
      if (count) { count.hidden = this.mode !== 'image' || this.galleryItems.length === 0; count.textContent = `${this.galleryIndex + 1} / ${this.galleryItems.length}`; }
      this.querySelectorAll('[data-rg-mode]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.rgMode === this.mode)));
      this.renderThumbs();
      if (scroll && this.mobile.matches && this.mode === 'image' && this.rail) this.rail.scrollTo({ left: this.galleryIndex * this.rail.clientWidth, behavior: 'instant' });
    }
    loadModel(model) {
      if (!model || model.dataset.loaded) return;
      model.dataset.loaded = 'true';
      const host = model.querySelector('[data-rg-model-host]');
      const content = model.querySelector('template').content.cloneNode(true);
      const viewer = content.querySelector('model-viewer');
      // Calibrated against the green striped hero image for this model's metre scale.
      if (/\/RG10000-V[12]\.glb(?:\?|$)/i.test(viewer?.getAttribute('src') || '')) {
        // Shopify's generated poster uses a different camera; do not flash it while loading.
        viewer.removeAttribute('poster');
        viewer.setAttribute('max-camera-orbit', 'auto auto 0.48m');
        viewer.setAttribute('camera-orbit', '30deg 70deg 0.32m');
        viewer.setAttribute('camera-target', '0.0035m 0.055m -0.0025m');
        viewer.setAttribute('field-of-view', '25deg');
      }
      viewer?.setAttribute('camera-controls', '');
      viewer?.setAttribute('interaction-prompt', 'none');
      // Connect only after the initial camera is configured.
      host.append(content);
      customElements.whenDefined('model-viewer').then(() => { if (this.isConnected) this.setupGuide(model, viewer); });
      viewer?.addEventListener('error', () => {
        model.querySelector('.rg-gallery__gesture')?.remove();
        if (model.querySelector('[role="status"]')) return;
        const message = document.createElement('p'); message.setAttribute('role', 'status');
        message.className = 'rg-gallery__error'; message.textContent = 'The 3D model could not load. Please refresh to try again.';
        model.append(message);
      }, { once: true });
      if (window.Shopify?.loadFeatures) Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0', onLoad: errors => {
        if (!errors && this.isConnected && viewer && window.Shopify.ModelViewerUI) new Shopify.ModelViewerUI(viewer);
      } }]);
    }
    setupGuide(model, viewer) {
      if (!viewer || this.guides.has(model)) return;
      const hand = model.querySelector('.rg-gallery__gesture');
      const guide = { viewer, hand, interacted: false, visible: false, running: false, ready: Boolean(viewer.loaded) };
      this.guides.set(model, guide);
      if (matchMedia('(prefers-reduced-motion: reduce)').matches || typeof viewer.interact !== 'function') {
        hand?.remove();
        viewer.setAttribute('interaction-prompt', 'auto');
        viewer.setAttribute('interaction-prompt-style', matchMedia('(prefers-reduced-motion: reduce)').matches ? 'basic' : 'wiggle');
        viewer.setAttribute('interaction-prompt-threshold', '0');
        return;
      }
      if (hand) {
        hand.className = 'rg-gallery__finger'; hand.slot = 'finger0';
        hand.querySelector('span')?.remove(); viewer.append(hand);
      }
      const start = () => {
        if (!this.isConnected || guide.interacted || !guide.visible || guide.running || this.active !== model || !guide.ready) return;
        if (hand) hand.hidden = false;
        guide.running = true;
        // Match the reference: a gentle gesture followed by an equally long rest.
        viewer.interact(2600, {
          x: { initialValue: .5, keyframes: [{ frames: 1, value: .45 }, { frames: 2, value: .55 }, { frames: 1, value: .5 }] },
          y: { initialValue: .5, keyframes: [{ frames: 1, value: .5 }] }
        });
        clearTimeout(guide.timer);
        guide.timer = setTimeout(() => { guide.running = false; guide.frame = requestAnimationFrame(() => { guide.frame = requestAnimationFrame(start); }); }, 5200);
      };
      guide.start = start;
      const ready = () => {
        guide.ready = true;
        guide.frame = requestAnimationFrame(start);
      };
      viewer.addEventListener('load', ready, { signal: this.abort.signal });
      // Shopify's older viewer can reveal the model before its material API finishes.
      viewer.addEventListener('poster-dismissed', ready, { signal: this.abort.signal });
      viewer.addEventListener('model-visibility', event => {
        if (event.detail.visible) ready();
      }, { signal: this.abort.signal });
      viewer.addEventListener('interact-stopped', event => {
        if (event.detail.source === 'user-interaction') this.stopGuide(model);
      }, { signal: this.abort.signal });
      viewer.addEventListener('camera-change', event => {
        if (event.detail.source === 'user-interaction') this.stopGuide(model);
      }, { signal: this.abort.signal });
      guide.observer = new IntersectionObserver(entries => {
        guide.visible = entries[0].isIntersecting;
        if (guide.visible) start();
      }, { threshold: .1 });
      guide.observer.observe(viewer);
    }
    stopGuide(model) {
      const guide = this.guides?.get(model);
      if (!guide) return;
      guide.interacted = true;
      clearTimeout(guide.timer);
      cancelAnimationFrame(guide.frame);
      if (guide.hand) guide.hand.hidden = true;
      guide.viewer.setAttribute('interaction-prompt', 'none');
    }
    disconnectedCallback() {
      this.guides?.forEach(guide => { guide.observer?.disconnect(); clearTimeout(guide.timer); cancelAnimationFrame(guide.frame); });
      if (this.expandedDialog?.open) this.expandedDialog.close();
      this.abort?.abort();
      this.unsubscribe?.();
      this.querySelectorAll('video').forEach(video => video.pause());
    }
  });
})();
